import { zodResolver } from '@hookform/resolvers/zod';
import { isAxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useChangePassword } from '@/entities/authentication/api/generated/api';
import type { ErrorResponse } from '@/entities/authentication/api/generated/model/errorResponse';
import { ErrorResponseErrorCode } from '@/entities/authentication/api/generated/model/errorResponseErrorCode';
import { Dialog, Form, InputField, toast } from '@/shared/ui';
import { type ChangePasswordFormData, changePasswordSchema } from '../validation';

type Props = {
  /** ダイアログの開閉状態 */
  open: boolean;
  /** ダイアログの開閉状態を変更するコールバック */
  onOpenChange: (open: boolean) => void;
};

const FORM_ID = 'change_password_dialog_form';

/**
 * パスワード変更ダイアログ
 *
 * 現在のパスワードと新しいパスワードを入力してパスワードを変更する
 */
export const ChangePasswordDialog = ({ open, onOpenChange }: Props) => {
  const { t } = useTranslation();

  const form = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      current_password: '',
      new_password: '',
      confirm_password: '',
    },
  });

  const changePasswordMutation = useChangePassword({
    request: { suppressToast: true },
    mutation: {
      onSuccess: () => {
        toast.success(t('auth.change-password.success'));
        onOpenChange(false);
        form.reset();
      },
      onError: (error) => {
        if (isAxiosError(error) && error.response) {
          const data = error.response.data as ErrorResponse;
          if (data.error_code === ErrorResponseErrorCode.VALIDATION_ERROR && data.message) {
            form.setError('new_password', {
              message: data.message,
            });
            return;
          }
        }
        toast.error(t('auth.change-password.failed'));
      },
    },
  });

  const handleSubmit = form.handleSubmit(
    ({ confirm_password: _, ...data }: ChangePasswordFormData) => {
      changePasswordMutation.mutate({ data });
    },
  );

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      form.reset();
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      title={t('auth.change-password.dialog-title')}
      width="s"
      confirmFormId={FORM_ID}
      disabled={changePasswordMutation.isPending}
    >
      <Form.Provider {...form}>
        <Form
          onSubmit={handleSubmit}
          id={FORM_ID}
          className="space-y-4"
          leavePageAlertCondition={null}
        >
          <InputField
            control={form.control}
            name="current_password"
            label={t('auth.change-password.current-password-label')}
            type="password"
            placeholder={t('auth.change-password.current-password-placeholder')}
            required
            autoComplete="current-password"
          />
          <InputField
            control={form.control}
            name="new_password"
            label={t('common.fields.new-password')}
            type="password"
            placeholder={t('auth.change-password.new-password-placeholder')}
            required
            supportText={t('auth.change-password.new-password-description')}
            showSupportText
            autoComplete="new-password"
          />
          <InputField
            control={form.control}
            name="confirm_password"
            label={t('auth.change-password.confirm-password-label')}
            type="password"
            required
            autoComplete="new-password"
          />
        </Form>
      </Form.Provider>
    </Dialog>
  );
};
